import { zonedAddDuration, zonedGetDay, zonedSubtractDuration } from '@csp/csp-common-date-util';
import { CspError, Maybe, StateAssert, Time, TimeStr, ZonedDateTime } from '@csp/csp-common-model';
import { executeFunctionInChunks } from '@csp/csp-common-util';
import { range } from 'lodash';
import { ScheduleWindow } from '../../model/ScheduleWindow';
import { DayOfWeek } from '../../model/schedulingModels/DayOfWeek';
import { Duration } from '../../model/schedulingModels/Duration';
import { Timing } from '../../model/schedulingModels/Timing';
import { TimingRepeatType } from '../../model/schedulingModels/TimingRepeatType';
import { UnitOfTime } from '../../model/schedulingModels/UnitOfTime';
import { ScheduleDateService } from '../../service/ScheduleDateService';
import { ScheduleValidationService } from '../../service/ScheduleValidationService';
import { SchedulingConst } from '../../type/SchedulingConst';
import { isValidStartAndEndAction } from '../../util/scheduleUtil';
import { DayAndTime } from '../model/DayAndTime';
import { RolloutTimingResult } from '../model/RolloutTimingResult';
import { TimeOfDay } from '../model/TimeOfDay';
import { TimingWindow } from '../model/TimingWindow';
import { WeekSchedule } from '../model/WeekSchedule';
import { getTimingRepeatPeriod } from '../util/getTimingRepeatPeriod';

const CHUNK_SIZE = 30;

const dayNumberMap: Record<DayOfWeek, number> = {
  MON: 0,
  TUE: 1,
  WED: 2,
  THU: 3,
  FRI: 4,
  SAT: 5,
  SUN: 6,
};

type RolloutTimingOptions = {
  timing: Timing;
  startActionOffsetTimestamp: ZonedDateTime;
  endActionOffsetTimestamp?: ZonedDateTime;
  fromRepeatIndex?: number;
  limitRepeat?: number;
};

type AsyncRolloutTimingOptions = RolloutTimingOptions & {
  pauseIntervalMs?: number;
};

type RolloutInfoOut = {
  isValidActions: boolean;
  repeatIndexes: Maybe<number[]>;
};

const rolloutInfo = ({
  timing,
  startActionOffsetTimestamp,
  endActionOffsetTimestamp,
  fromRepeatIndex,
  limitRepeat,
}: RolloutTimingOptions): RolloutInfoOut => {
  ScheduleValidationService.validateTiming(timing);
  const isValidActions = isValidStartAndEndAction(startActionOffsetTimestamp, endActionOffsetTimestamp);
  const repeatIndexes = isValidActions
    ? getRepeatIndexes({
        timing,
        startActionOffsetTimestamp,
        endActionOffsetTimestamp,
        fromRepeatIndex,
        limitRepeat,
      })
    : [];

  return { isValidActions, repeatIndexes };
};

const rolloutTiming = async (options: AsyncRolloutTimingOptions): Promise<RolloutTimingResult> => {
  const { timing, startActionOffsetTimestamp, endActionOffsetTimestamp } = options;
  const windows: TimingWindow[] = [];

  const { repeatIndexes, isValidActions } = rolloutInfo(options);

  if (isValidActions && repeatIndexes) {
    if (timing.repeat.repeatType === TimingRepeatType.NONE) {
      if (options.fromRepeatIndex === 0) {
        windows.push(getRequestWindowFromNonRepeating(timing, startActionOffsetTimestamp, endActionOffsetTimestamp));
      }
    } else if (
      timing.repeat.repeatType === TimingRepeatType.DAILY ||
      timing.repeat.repeatType == TimingRepeatType.PERIOD
    ) {
      const requestWindows = await executeFunctionInChunks(
        CHUNK_SIZE,
        repeatIndexes,
        indexes =>
          indexes.map(repeatIndex =>
            timing.repeat.repeatType == TimingRepeatType.DAILY
              ? getRequestWindowFromRepeatingDaily(timing, startActionOffsetTimestamp, repeatIndex)
              : getRequestWindowFromRepeatingPeriod(timing, startActionOffsetTimestamp, repeatIndex),
          ),
        options.pauseIntervalMs,
      );
      windows.push(...requestWindows);
    } else {
      throw CspError.badState(`Unsupported TimingRepeatType: ${timing.repeat.repeatType}`);
    }
  }

  return RolloutTimingResult.from(windows, timing.repeat.repeatType);
};

const getRequestWindowFromNonRepeating = (
  timing: Timing,
  startActionOffsetTimestamp: ZonedDateTime,
  endActionOffsetTimestamp?: ZonedDateTime,
): TimingWindow => {
  const interval = calculateWindow(startActionOffsetTimestamp, timing.windowBefore, timing.windowAfter);

  // If the request window end is not set from windowAfter, use the end action timestamp.
  // If window end is still undefined, it means that the window has no end date restriction.
  interval.windowEnd = interval.windowEnd ?? endActionOffsetTimestamp;

  return TimingWindow.from(timing, interval, 0);
};

const getRequestWindowFromRepeatingDaily = (
  timing: Timing,
  startActionOffsetTimestamp: ZonedDateTime,
  repeatIndex: number,
): TimingWindow => {
  const daysOfWeek = timing.repeat.daysOfWeek ?? SchedulingConst.DEFAULT_DAYS_OF_WEEK;
  const timesOfDay = timing.repeat.timesOfDay ?? [];

  StateAssert.notEmpty(daysOfWeek, 'daysOfWeek requires at least one day');

  // The times to schedule in any given week
  const weekSchedule: WeekSchedule = getWeekSchedule(daysOfWeek, timesOfDay);

  // first index of week schedule after start action timestamp
  const numPastWindowsInWeekSchedule = getNumPastWindowsInWeekSchedule(weekSchedule, startActionOffsetTimestamp);

  const numWindowsInAWeek = weekSchedule.length;

  // How many calender weeks from start action date to repeatIndex
  const numWeeksToRepeatIndex = Math.floor((repeatIndex + numPastWindowsInWeekSchedule) / numWindowsInAWeek);

  // What week schedule index matches the repeatIndex
  const repeatIndexWindow = (repeatIndex + numPastWindowsInWeekSchedule) % numWindowsInAWeek;
  const weekScheduleWindowOfRepeatIndex = weekSchedule[repeatIndexWindow];
  StateAssert.isDefined(weekScheduleWindowOfRepeatIndex);

  const weekOfRepeatIndex = zonedAddDuration(startActionOffsetTimestamp, { weeks: numWeeksToRepeatIndex });
  const dateTimePeriodOfRepeatIndex = setTimeOfDay(weekOfRepeatIndex, weekScheduleWindowOfRepeatIndex);

  const interval = calculateWindow(dateTimePeriodOfRepeatIndex, timing.windowBefore, timing.windowAfter);

  return TimingWindow.from(timing, interval, repeatIndex);
};

const setTimeOfDay = (dateTime: ZonedDateTime, dayAndTime: DayAndTime): ZonedDateTime => {
  const startOfWeek = ScheduleDateService.startOf(dateTime, UnitOfTime.WK);

  // Add days, hours and minutes from the weekschedule to the correct week
  return zonedAddDuration(startOfWeek, {
    days: dayAndTime.dayIndex,
    hours: dayAndTime.time ? ScheduleDateService.getHoursOfTimeStr(dayAndTime.time) : undefined,
    minutes: dayAndTime.time ? ScheduleDateService.getMinutesOfTimeStr(dayAndTime.time) : undefined,
  });
};

const getRequestWindowFromRepeatingPeriod = (
  timing: Timing,
  startActionOffsetTimestamp: ZonedDateTime,
  repeatIndex: number,
): TimingWindow => {
  const period = getTimingRepeatPeriod(timing);

  StateAssert.notNull(period, 'Repeating with period requires a repeat.period');

  const repeatPeriodOffset: Duration = getRepeatPeriodOffset(period, repeatIndex);

  // Tsa    Tsa+offset   repeat 1    repeat 2...             repeat N
  // |---------|-----------|-----------|-----------|-----------|----------->
  const repeatPeriodOffsetDate = zonedAddDuration(
    startActionOffsetTimestamp,
    ScheduleDateService.toDateTimeDuration(repeatPeriodOffset),
  );

  // Tsa    Tsa+offset                     windowBefore  repeat N   windowAfter
  // |---------|--------------------------------|-----------|----------|--->
  const interval = calculateWindow(repeatPeriodOffsetDate, timing.windowBefore, timing.windowAfter);

  return TimingWindow.from(timing, interval, repeatIndex);
};

const getRepeatIndexes = ({
  timing,
  startActionOffsetTimestamp,
  endActionOffsetTimestamp,
  fromRepeatIndex = 0,
  limitRepeat = 1,
}: RolloutTimingOptions): number[] => {
  StateAssert.isTrue(limitRepeat >= 0, 'limitRepeat must be equal or larger than 0');
  // Do not allow negative repeatIndex.
  fromRepeatIndex = Math.max(fromRepeatIndex, 0);

  const toRepeatIndexExclusive = endActionOffsetTimestamp
    ? Math.min(
        getNextRepeatIndexFromDate({
          timing,
          startActionOffsetTimestamp,
          dateTime: endActionOffsetTimestamp,
        }),
        fromRepeatIndex + limitRepeat,
      )
    : fromRepeatIndex + limitRepeat;

  return range(fromRepeatIndex, toRepeatIndexExclusive);
};

type GetNextRepeatIndexFromDateArgs = {
  timing: Timing;
  startActionOffsetTimestamp: ZonedDateTime;
  dateTime: ZonedDateTime;
};

const getLowestRepeatIndexOverlappingWithDate = ({
  timing,
  startActionOffsetTimestamp,
  dateTime,
}: GetNextRepeatIndexFromDateArgs): number => {
  const earliestReferenceDate = timing.windowAfter
    ? getWindowStart(dateTime, timing.windowAfter)
    : startActionOffsetTimestamp;

  // Make it inclusively match request windows with reference dates equal to the timestamp
  earliestReferenceDate.unixTimeMillis--;
  return getNextRepeatIndexFromDate({ timing, startActionOffsetTimestamp, dateTime: earliestReferenceDate });
};

const getHighestRepeatIndexOverlappingWithDate = ({
  timing,
  startActionOffsetTimestamp,
  dateTime,
}: GetNextRepeatIndexFromDateArgs): number => {
  const latestReferenceDate = getWindowEnd(dateTime, timing.windowBefore);
  return getNextRepeatIndexFromDate({ timing, startActionOffsetTimestamp, dateTime: latestReferenceDate });
};

const getNextRepeatIndexFromDate = ({
  timing,
  startActionOffsetTimestamp,
  dateTime,
}: GetNextRepeatIndexFromDateArgs): number => {
  if (timing.repeat.repeatType === TimingRepeatType.PERIOD) {
    return getNextPeriodicalRepeatIndexFromDate(timing, startActionOffsetTimestamp, dateTime);
  } else if (timing.repeat.repeatType === TimingRepeatType.DAILY) {
    return getNextDailyRepeatIndexFromDate(timing, startActionOffsetTimestamp, dateTime);
  } else if (timing.repeat.repeatType === TimingRepeatType.NONE) {
    // non-repeating request only have one window, 0.
    return 0;
  } else {
    throw CspError.badState(`Unsupported TimingRepeatType: ${timing.repeat.repeatType}`);
  }
};

const getNextPeriodicalRepeatIndexFromDate = (
  timing: Timing,
  startActionOffsetTimestamp: ZonedDateTime,
  dateTime: ZonedDateTime,
): number => {
  const period = getTimingRepeatPeriod(timing);
  // if the requested date is before the start action offset date,
  // 0 must be the first index
  if (ScheduleDateService.isBeforeDateTime(dateTime, startActionOffsetTimestamp)) {
    return 0;
  }

  StateAssert.notNull(period);
  const offsetTimestampToRequestedDateDuration = ScheduleDateService.differenceInUnitOfTime(
    {
      start: startActionOffsetTimestamp,
      end: dateTime,
    },
    period.unit,
  );

  const numRepeatsToRequestDate = Math.floor(offsetTimestampToRequestedDateDuration.value / period.value);
  return numRepeatsToRequestDate + 1;
};

const getNextDailyRepeatIndexFromDate = (
  timing: Timing,
  startActionOffsetTimestamp: ZonedDateTime,
  dateTime: ZonedDateTime,
): number => {
  const daysOfWeek = timing.repeat?.daysOfWeek ?? SchedulingConst.DEFAULT_DAYS_OF_WEEK;
  const timesOfDay = timing.repeat?.timesOfDay ?? [];
  // if the requested date is before the start action offset date,
  // 0 must be the first index
  if (ScheduleDateService.isBeforeDateTime(dateTime, startActionOffsetTimestamp)) {
    return 0;
  }

  const diffInWeeks = ScheduleDateService.differenceInCalendarWeeks({
    start: startActionOffsetTimestamp,
    end: dateTime,
  });

  const weekSchedule: WeekSchedule = getWeekSchedule(daysOfWeek, timesOfDay);

  // first index of week schedule after start action timestamp
  const numPastWindowsFirstWeek = getNumPastWindowsInWeekSchedule(weekSchedule, startActionOffsetTimestamp);

  // first index of week schedule after requested date time
  const numPastWindowsRequestWeek = getNumPastWindowsInWeekSchedule(weekSchedule, dateTime);

  const numWindowsInAWeek = weekSchedule.length;

  const windowsLeftInFirstWeek = numWindowsInAWeek - numPastWindowsFirstWeek;
  const windowsPassedInRequestedWeek = numPastWindowsRequestWeek;

  if (diffInWeeks.value === 0) {
    return windowsPassedInRequestedWeek - numPastWindowsFirstWeek;
  } else {
    return windowsLeftInFirstWeek + windowsPassedInRequestedWeek + (diffInWeeks.value - 1) * numWindowsInAWeek;
  }
};

const getRepeatPeriodOffset = (period: Duration, repeatIndex: number): Duration => ({
  value: period.value * repeatIndex,
  unit: period.unit,
});

const getIndexOfDayOfWeek = (dayOfWeek: DayOfWeek): number => {
  const dayIndex = dayNumberMap[dayOfWeek];
  StateAssert.notNull(dayIndex, `Failed to map index '${dayOfWeek}' to day index`);
  return dayIndex;
};

const getWeekSchedule = (daysOfWeek: DayOfWeek[], timesOfDay: TimeStr[]): WeekSchedule =>
  daysOfWeek.flatMap(day => {
    if (timesOfDay.length === 0) {
      return [{ dayIndex: getIndexOfDayOfWeek(day) }];
    } else {
      return timesOfDay.map(time => ({ dayIndex: getIndexOfDayOfWeek(day), time }));
    }
  });

const getWindowStart = (periodDate: ZonedDateTime, windowBefore: Duration): ZonedDateTime => {
  const periodWindowBefore = zonedSubtractDuration(periodDate, ScheduleDateService.toDateTimeDuration(windowBefore));
  return ScheduleDateService.startOf(periodWindowBefore, windowBefore.unit);
};

const getWindowEnd = (periodDate: ZonedDateTime, windowAfter: Duration): ZonedDateTime => {
  const periodWindowAfter = zonedAddDuration(periodDate, ScheduleDateService.toDateTimeDuration(windowAfter));
  return ScheduleDateService.startOfNext(periodWindowAfter, windowAfter.unit);
};

const calculateWindow = (periodDate: ZonedDateTime, windowBefore: Duration, windowAfter?: Duration): ScheduleWindow => {
  // Calculate start and end.
  // So far we have used millisecond resolution in our calculations. But when using
  // for example windowAfter 1M, we interpret this to be until "end of next month".
  // Same for windowBefore 1H, we interpret this to be start of previous hour.
  // The unit of windowBefore and windowAfter determines the "resolution".
  const windowStart = getWindowStart(periodDate, windowBefore);
  const windowEnd = windowAfter ? getWindowEnd(periodDate, windowAfter) : undefined;
  const windowReferenceTimestamp = periodDate;

  return {
    windowStart,
    windowReferenceTimestamp,
    windowEnd,
  };
};

const getNumPastWindowsInWeekSchedule = (weekSchedule: WeekSchedule, zonedDateTime: ZonedDateTime): number => {
  const timeOfDay = TimeOfDay.from(zonedGetDay(zonedDateTime), ScheduleDateService.getTimeStr(zonedDateTime));
  const nextWeekScheduleIndex = weekSchedule.findIndex(window => {
    if (window.dayIndex === timeOfDay.dayIndex) {
      if (window.time) {
        const windowTime = Time.from(window.time);
        const timeOfDayTime = Time.from(timeOfDay.time);
        return Time.compare(windowTime, timeOfDayTime) > 0;
      } else {
        return true;
      }
    } else {
      return window.dayIndex > timeOfDay.dayIndex;
    }
  });
  return nextWeekScheduleIndex !== -1 ? nextWeekScheduleIndex : weekSchedule.length;
};

export const RequestTimingService = {
  rolloutTiming,
  getNextRepeatIndexFromDate,
  getLowestRepeatIndexOverlappingWithDate,
  getHighestRepeatIndexOverlappingWithDate,
};
